// client/src/hooks/useTypingIndicators.js

import { useState, useEffect, useCallback, useRef } from 'react'
import ChatService from '../services/ChatService'

/**
 * Custom hook for managing typing indicators
 * @param {Object} activeChannel - Currently active channel
 * @param {boolean} chatConnected - Whether chat service is connected
 * @returns {Object} Typing state and methods
 */
export default function useTypingIndicators(activeChannel, chatConnected) {
  // channelId -> Map<playerId, { playerId, playerName }>
  const [typingUsers, setTypingUsers] = useState(new Map())
  const typingTimeoutsRef = useRef(new Map()) // playerId -> timeout

  // Handle incoming typing events
  const handleTypingEvent = useCallback((data) => {
    const { channelId, playerId, playerName, isTyping } = data

    setTypingUsers((prevTyping) => {
      const newTyping = new Map(prevTyping)
      if (!newTyping.has(channelId)) {
        newTyping.set(channelId, new Map())
      }

      const channelTyping = new Map(newTyping.get(channelId))

      if (isTyping) {
        channelTyping.set(playerId, { playerId, playerName })

        // Clear any existing timeout for this user
        if (typingTimeoutsRef.current.has(playerId)) {
          clearTimeout(typingTimeoutsRef.current.get(playerId))
        }

        // Auto-remove typing indicator after 5 seconds
        const timeout = setTimeout(() => {
          setTypingUsers((prev) => {
            const updated = new Map(prev)
            if (updated.has(channelId)) {
              const inner = new Map(updated.get(channelId))
              inner.delete(playerId)
              updated.set(channelId, inner)
            }
            return updated
          })
          typingTimeoutsRef.current.delete(playerId)
        }, 5000)

        typingTimeoutsRef.current.set(playerId, timeout)
      } else {
        // Remove typing indicator
        channelTyping.delete(playerId)

        // Clear timeout
        if (typingTimeoutsRef.current.has(playerId)) {
          clearTimeout(typingTimeoutsRef.current.get(playerId))
          typingTimeoutsRef.current.delete(playerId)
        }
      }

      newTyping.set(channelId, channelTyping)
      return newTyping
    })
  }, [])

  // Set up typing event listener
  useEffect(() => {
    if (!chatConnected) return

    const unsubscribe = ChatService.onTyping(handleTypingEvent)

    // Capture the ref value for use in cleanup
    const timeouts = typingTimeoutsRef.current

    return () => {
      unsubscribe()
      // Clear all timeouts
      timeouts.forEach((timeout) => clearTimeout(timeout))
      timeouts.clear()
    }
  }, [chatConnected, handleTypingEvent])

  // Get typing users for the active channel
  const getTypingUsersForChannel = useCallback(
    (channelId) => {
      if (!channelId || !typingUsers.has(channelId)) return []
      const map = typingUsers.get(channelId)
      return Array.from(map.values())
    },
    [typingUsers]
  )

  // Start typing for active channel
  const startTyping = useCallback(() => {
    if (activeChannel && chatConnected) {
      ChatService.startTyping(activeChannel.id)
    }
  }, [activeChannel, chatConnected])

  // Stop typing for active channel
  const stopTyping = useCallback(() => {
    if (activeChannel && chatConnected) {
      ChatService.stopTyping(activeChannel.id)
    }
  }, [activeChannel, chatConnected])

  return {
    getTypingUsersForChannel,
    startTyping,
    stopTyping,
  }
}
